import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ChevronLeft, EyeOff, FileSignature, ReceiptText } from 'lucide-react';
import {
  MOBILE_OVERVIEW_CARDS,
  type OverviewCard,
  type OverviewCardId,
  type OverviewCardOf,
  type OverviewCardPrefs,
} from '@reos/today-metrics';
import { formatNumber, formatTime } from '@reos/i18n';
import { OVERVIEW_QUERY_KEY, fetchOverview, saveOverviewCards } from '../lib/overview-api.js';
import { stageHue, stageLabel } from '../lib/case-stages.js';
import { transitionTask } from '../lib/tasks-api.js';
import { caseProgressView } from '../lib/case-progress.js';
import { DocumentCoverPlaceholder, PropertyImage } from './PropertyImage.js';
import { useLocale, useT } from '../lib/i18n.js';

/**
 * Krok GRAF2 — TABULE KARET obrazovky „Přehled".
 *
 * Rozvrh, poradi i vyber pro telefon podle schvalene predlohy
 * (`/srv/debata/maklerov-grafika-v3/VYSLEDEK.png` a jeji svetla varianta).
 * Data chodi JEDNIM dotazem (`GET /v1/overview`), ne dvanacti — viz hlavicka
 * `apps/api/src/routes/overview.ts`.
 *
 * TRI STAVY KAZDE KARTY (zadani): `data` kresli cisla, `empty` rekne, ze
 * zdroj je prazdny, `disconnected` rekne, ze zdroj neni pripojeny, a nabidne
 * kam pro to jit. NIKDY se nekresli nula jako fakt — o tom, co karta rekne,
 * rozhoduje `card.state` ze serveru, ne pritomnost cisla.
 */

/** Sirka karty v dvanactisloupcove mrizce (predloha). */
const CARD_SPAN: Readonly<Record<OverviewCardId, number>> = {
  burning: 12,
  nextStep: 8,
  today: 4,
  cases: 4,
  commission: 4,
  demands: 4,
  portals: 3,
  officeSite: 3,
  adSpend: 3,
  radar: 3,
  brokers: 6,
  credits: 6,
};

/** Kam karta vede, kdyz na ni clovek klikne (cil akce i stavu „nepripojeno"). */
const CARD_LINK: Readonly<Record<OverviewCardId, string>> = {
  burning: '/ukoly',
  nextStep: '/zakazky',
  today: '/kalendar',
  cases: '/zakazky',
  commission: '/fakturace',
  demands: '/poptavky',
  portals: '/portaly',
  officeSite: '/nastaveni',
  adSpend: '/naklady',
  radar: '/akvizice',
  brokers: '/nastaveni',
  credits: '/nastaveni',
};

/**
 * Krok GRAF6 nalez 4 — KARTA BEZ DAT MA TVAR PREDLOHY. Do GRAF6 byla prazdna
 * karta jen veta („Kancelář zatím nemá zapsanou provizi"), takze nova kancelar
 * videla misto tabule sloupec textu. Predloha ma vzdy velke cislo a pod nim
 * graf — u prazdne karty tedy NULU a tlumeny graf.
 *
 * Tvar NENI u vsech karet stejny, protoze cislo musi byt pravda:
 *  - `figure-chart` kresli nulu i tlumeny graf — karta, ktera graf ma i s daty,
 *  - `figure`       kresli jen nulu — karta s dominantnim cislem bez grafu,
 *  - karta, ktera ani s daty cislo nema (program dne, seznam makleru, dalsi
 *    krok), zustava u vety: „0" by u seznamu nic nemerila.
 */
const CARD_EMPTY_SHAPE: Partial<Readonly<Record<OverviewCardId, 'figure' | 'figure-chart'>>> = {
  cases: 'figure-chart',
  commission: 'figure-chart',
  portals: 'figure-chart',
  demands: 'figure',
  radar: 'figure',
  officeSite: 'figure',
  adSpend: 'figure',
};

/** Pocet sloupcu tlumeneho grafu — tolik, kolik jich kresli karta s daty. */
const EMPTY_CHART_BARS = [0, 1, 2, 3, 4] as const;

/**
 * Krok GRAF4 nalez 7 — TONOVANA PLOCHA KARTY. Predloha dava vlastni barvu
 * DVEMA kartam (Provize limetka, Poptávky šeřík), zbytek nechava sklem.
 */
const CARD_TINT: Partial<Readonly<Record<OverviewCardId, string>>> = {
  commission: 'rh-ov-card--accent',
  demands: 'rh-ov-card--broker',
};

function cardText(
  t: ReturnType<typeof useT>,
  id: OverviewCardId,
  part: 'title' | 'empty' | 'disconnected' | 'action',
): string {
  return t(`webAdmin.prehled.cards.${id}.${part}`);
}

/**
 * Krok GRAF4 nalez 5 — CISLA S MEZEROU TISICU. Jedno misto pro cely soubor:
 * `formatNumber` z `@reos/i18n` (tentyz formatovac, jaky sazi pocty na
 * ostatnich obrazovkach), a jedna prevodni tabulka `UiLocale` → BCP 47.
 * Predloha pise „3 820" a „1 240"; `String(3820)` dava „3820".
 */
function intlTag(locale: ReturnType<typeof useLocale>): string {
  return locale === 'cs' ? 'cs-CZ' : 'en-GB';
}

function useCount(): (value: number) => string {
  const locale = useLocale();
  return (value: number) => formatNumber(value, { locale: intlTag(locale) });
}

/**
 * Obal karty: nadpis, nabidka „posunout vlevo / skrýt" a obsah. Nabidka je
 * tu, a ne v kazde karte, aby preskladani fungovalo stejne u vsech dvanacti.
 */
function CardShell({
  id,
  title,
  tools,
  children,
}: {
  readonly id: OverviewCardId;
  readonly title: string;
  readonly tools: React.ReactNode;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  /*
   * Vyber pro telefon (predloha: Co hoří · Další krok · Dnes · Zakázky ·
   * Inzerce) je TRIDA, ne `window.matchMedia`: rozhodnout to v JS by znamenalo
   * prekreslit tabuli pri otoceni zarizeni a nechat ji do prvniho mereni
   * sirky prazdnou. Mnozinu drzi `MOBILE_OVERVIEW_CARDS` v @reos/today-metrics.
   */
  const desktopOnly = MOBILE_OVERVIEW_CARDS.includes(id) ? '' : ' rh-ov-cell--desktop';
  const tint = CARD_TINT[id];
  return (
    <div
      className={`rh-ov-cell${desktopOnly}`}
      style={{ ['--rh-ov-span' as string]: String(CARD_SPAN[id]) }}
    >
      <section
        className={`rh-a2-card rh-ov-card${tint === undefined ? '' : ` ${tint}`}`}
        data-testid={`prehled-karta-${id}`}
      >
        <div className="rh-ov-card__head">
          <h2 className="rh-ov-card__title">{title}</h2>
          <div className="rh-ov-card__tools">{tools}</div>
        </div>
        {children}
      </section>
    </div>
  );
}

/**
 * Sloupcovy graf: vysky jsou POMER k nejvyssimu sloupci, ne pixely predlohy.
 *
 * Krok GRAF4 nalez 4: sloupec si smi prinest ODSTIN (`hue`) — u karty
 * „Zakázky" je to `stageHue(stage)`, tedy tataz identita faze, jakou nese
 * kapsle v seznamu. Kdyz `hue` chybi (portaly, provize), zustava akcent.
 */
function Bars({
  items,
}: {
  readonly items: readonly { label: string; value: number; hue?: string | null }[];
}): React.JSX.Element {
  const max = items.reduce((top, item) => Math.max(top, item.value), 0);
  return (
    <div className="rh-ov-bars">
      {items.map((item) => {
        const hue = item.hue ?? null;
        return (
          <div className="rh-ov-bar" key={item.label}>
            <div
              className={`rh-ov-bar__fill${hue === null ? '' : ` rh-ov-bar__fill--${hue}`}`}
              style={{ height: `${String(max === 0 ? 4 : Math.round((item.value / max) * 64) + 4)}px` }}
              data-testid={`prehled-sloupec-${item.label}`}
            />
            <span className="rh-ov-bar__label">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Krok GRAF4 nalez 2 — „Co hoří" NENI KARTA. Predloha ma pres celou sirku
 * korálový pruh (ikona · text · tlacitko „Otevřít"), ne tmavou kartu
 * s nadpisem. Komponenta proto nekresli ani nadpis — vetu nese pruh sam.
 */
function BurningCard({ card }: { readonly card: OverviewCardOf<'burning'> }): React.JSX.Element | null {
  const t = useT();
  if (card.data === null) return null;
  const parts = [
    card.data.overdueInvoices > 0
      ? t('webAdmin.prehled.burning.invoices', { count: card.data.overdueInvoices })
      : null,
    card.data.contractsToSign > 0
      ? t('webAdmin.prehled.burning.contracts', { count: card.data.contractsToSign })
      : null,
    card.data.demandsWithoutReply > 0
      ? t('webAdmin.prehled.burning.demands', { count: card.data.demandsWithoutReply })
      : null,
  ].filter((part): part is string => part !== null);
  return (
    <div className="rh-ov-burning" role="alert">
      <AlertCircle size={20} strokeWidth={2} aria-hidden />
      {/* Predloha pise „Co hoří: 2 faktury … · 1 smlouva …" jednou vetou —
          pruh nema hlavicku karty, takze nazev patri do textu. */}
      <span className="rh-ov-burning__text" data-testid="prehled-co-hori-text">
        {t('webAdmin.prehled.burning.lead', { what: parts.join(' · ') })}
      </span>
      <Link to="/ukoly" className="rh-a2-btn rh-ov-burning__action">
        {t('webAdmin.prehled.burning.action')}
      </Link>
    </div>
  );
}

function NextStepCard({ card }: { readonly card: OverviewCardOf<'nextStep'> }): React.JSX.Element | null {
  const t = useT();
  if (card.data === null) return null;
  const { kind, caseId, propertyId, subject, coverUrl, lat, lon, stage } = card.data;
  /*
   * Krok GRAF2 (slouceni s Dneškem): „co chybí" u rozpracovane zakazky
   * pocita `caseProgressView` — TATAZ funkce, jakou ukazuje seznam i detail
   * zakazky. Druha veta o postupu by se s nimi rozesla.
   */
  const note =
    kind === 'resume' && stage !== null ? caseProgressView(stage, {}).missing : null;
  return (
    <>
      {/*
        Predloha ma nad textem fotku nemovitosti pres celou sirku karty.
        Krok PIL5 bod 2 (prevzato z Dneška): smlouva nebo faktura BEZ zakazky
        nema ke ktere nemovitosti nahled dohledat — misto sede plochy „Fotka
        po nahrání" (ta slibuje fotku, ktera uz nikdy nepribude) kresli karta
        parcelni motiv s typem dokladu.
      */}
      {propertyId !== null ? (
        <PropertyImage
          photoUrl={coverUrl}
          propertyId={propertyId}
          lat={lat}
          lon={lon}
          size={{ width: 640, height: 280 }}
          className="rh-ov-photo"
          testIdPrefix="prehled-dalsi-krok"
        />
      ) : kind === 'contract' || kind === 'invoice' ? (
        <DocumentCoverPlaceholder
          icon={
            kind === 'contract' ? (
              <FileSignature size={28} strokeWidth={1.8} />
            ) : (
              <ReceiptText size={28} strokeWidth={1.8} />
            )
          }
          label={t(
            kind === 'contract'
              ? 'webAdmin.caseThumb.documentKindContract'
              : 'webAdmin.caseThumb.documentKindInvoice',
          )}
          testIdPrefix="prehled-dalsi-krok"
        />
      ) : null}
      <p className="rh-ov-card__sub">{t(`webAdmin.prehled.nextStep.${kind}`)}</p>
      {/*
        Krok GRAF6 nalez 5 — PREDMET NENI CISLO. Do GRAF6 nesl nazev smlouvy
        tridu `rh-ov-figure` (stupen velkeho cisla, 36 px), takze dvouradkovy
        nazev delal z karty nejvyssi prvek obrazovky (zive 444 px proti ~150 px
        predlohy). Predloha sazi predmet jako TUCNY NADPIS karty.
      */}
      {subject !== '' ? (
        <p className="rh-ov-subject" data-testid="prehled-dalsi-krok-predmet">
          {subject}
        </p>
      ) : null}
      {note !== null ? (
        <p className="rh-ov-note" data-testid="prehled-dalsi-krok-chybi">
          {note}
        </p>
      ) : null}
      {kind === 'firstCase' ? (
        <Link to="/zakazky/novy" className="rh-a2-btn rh-a2-btn--primary" data-testid="prehled-dalsi-krok-nova">
          {t('webAdmin.prehled.nextStep.action.firstCase')}
        </Link>
      ) : caseId !== null ? (
        <Link
          to="/zakazky/$id"
          params={{ id: caseId }}
          className="rh-a2-btn rh-a2-btn--primary"
          data-testid="prehled-dalsi-krok-akce"
        >
          {t(`webAdmin.prehled.nextStep.action.${kind}`)}
        </Link>
      ) : null}
    </>
  );
}

function TodayCard({
  card,
  onTaskDone,
  busyTaskId,
}: {
  readonly card: OverviewCardOf<'today'>;
  readonly onTaskDone: (taskId: string) => void;
  readonly busyTaskId: string | null;
}): React.JSX.Element | null {
  const t = useT();
  const locale = useLocale();
  if (card.data === null) return null;
  // Tentyz zapis casu jako agenda kalendare a program Dneška (`formatTime`).
  const tag = intlTag(locale);
  return (
    <>
      <ul className="rh-ov-rows" data-testid="prehled-dnes-seznam">
        {card.data.entries.map((entry) => {
          // Id ukolu je v `entry.id` za predponou druhu (`task-<id>`).
          const taskId = entry.kind === 'task' ? entry.id.replace('task-', '') : null;
          return (
            <li className="rh-ov-row" key={entry.id}>
              <span className="rh-ov-row__time">
                {entry.at === null ? '' : formatTime(new Date(entry.at), { locale: tag })}
              </span>
              <span className="rh-ov-row__dot" aria-hidden />
              <span>{entry.title}</span>
              {/*
                Krok GRAF2 (slouceni s Dneškem): ukol jde odskrtnout primo
                z programu — tentyz prechod (`transitionTask`) jako pretazeni
                na tabuli Úkoly. Udalost kalendare takovou akci nema.
              */}
              {taskId !== null ? (
                <button
                  type="button"
                  className="rh-a2-btn rh-a2-btn--ghost"
                  disabled={busyTaskId === taskId}
                  data-testid={`prehled-dnes-hotovo-${taskId}`}
                  onClick={() => {
                    onTaskDone(taskId);
                  }}
                >
                  {t('webAdmin.prehled.today.done')}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="rh-a2-card__foot">
        <Link to="/kalendar" className="rh-a2-link" data-testid="prehled-dnes-kalendar">
          {t('webAdmin.prehled.today.allMeetings')}
        </Link>
        <span aria-hidden="true"> · </span>
        <Link to="/ukoly" className="rh-a2-link" data-testid="prehled-dnes-ukoly">
          {t('webAdmin.prehled.today.allTasks')}
        </Link>
      </p>
    </>
  );
}

function CasesCard({ card }: { readonly card: OverviewCardOf<'cases'> }): React.JSX.Element | null {
  const t = useT();
  const count = useCount();
  if (card.data === null) return null;
  return (
    <>
      <p className="rh-ov-figure" data-testid="prehled-zakazky-pocet">
        {count(card.data.open)}
        <span className="rh-ov-figure__note"> {t('webAdmin.prehled.cards.cases.unit')}</span>
      </p>
      {/* Popisek faze je `stageLabel` a odstin `stageHue` — tytez nazvy
          sloupcu i tytez barvy jako kanban a kapsle v seznamu. */}
      <Bars
        items={card.data.byStage.map((row) => ({
          label: stageLabel(row.stage),
          value: row.count,
          hue: stageHue(row.stage),
        }))}
      />
    </>
  );
}

function DemandsCard({ card }: { readonly card: OverviewCardOf<'demands'> }): React.JSX.Element | null {
  const t = useT();
  const count = useCount();
  if (card.data === null) return null;
  const { funnel } = card.data;
  return (
    <>
      <p className="rh-ov-figure" data-testid="prehled-poptavky-pocet">
        {count(card.data.last30)}
      </p>
      <ul className="rh-ov-rows">
        <li className="rh-ov-row">{t('webAdmin.prehled.demands.demands', { count: funnel.demands })}</li>
        <li className="rh-ov-row">{t('webAdmin.prehled.demands.viewings', { count: funnel.viewings })}</li>
        <li className="rh-ov-row">{t('webAdmin.prehled.demands.offers', { count: funnel.offers })}</li>
        <li className="rh-ov-row">
          {t('webAdmin.prehled.demands.reservations', { count: funnel.reservations })}
        </li>
      </ul>
    </>
  );
}

function PortalsCard({ card }: { readonly card: OverviewCardOf<'portals'> }): React.JSX.Element | null {
  const t = useT();
  const count = useCount();
  if (card.data === null) return null;
  const lastWeek = card.data.weeks[card.data.weeks.length - 1];
  const views = (lastWeek?.byPortal ?? []).reduce((sum, row) => sum + row.views, 0);
  return (
    <>
      <p className="rh-ov-card__sub">{t('webAdmin.prehled.portals.viewsThisWeek')}</p>
      <p className="rh-ov-figure" data-testid="prehled-portaly-zobrazeni">
        {count(views)}
      </p>
      <Bars
        items={card.data.weeks.map((week) => ({
          label: week.weekStart.slice(5),
          value: week.byPortal.reduce((sum, row) => sum + row.views, 0),
        }))}
      />
      {card.data.withoutReaction14d > 0 ? (
        <p className="rh-ov-note" data-testid="prehled-portaly-bez-reakce">
          {t('webAdmin.prehled.portals.withoutReaction', { count: card.data.withoutReaction14d })}
        </p>
      ) : null}
    </>
  );
}

function RadarCard({ card }: { readonly card: OverviewCardOf<'radar'> }): React.JSX.Element | null {
  const t = useT();
  const count = useCount();
  if (card.data === null) return null;
  return (
    <>
      <p className="rh-ov-figure" data-testid="prehled-radar-pocet">
        {count(card.data.newLeads)}
      </p>
      <p className="rh-ov-card__sub">{t('webAdmin.prehled.cards.radar.unit')}</p>
      <Link to="/akvizice" className="rh-a2-btn rh-a2-btn--primary">
        {cardText(t, 'radar', 'action')}
      </Link>
    </>
  );
}

/** Castka se sazi pres `Intl` z numeric retezce — nikdy se nepocita v `number`. */
function Money({ value }: { readonly value: { amount: string; currency: string } }): React.JSX.Element {
  const locale = useLocale();
  /*
   * Krok GRAF4 nalez 5: formatovac dostava BCP 47 znacku (`cs-CZ`), ne `cs`.
   * `Intl` sice `cs` zna, ale jediná prevodni tabulka v souboru (`intlTag`)
   * drzi castku, pocty i cas na tomtez locale — predloha pise „186 400 Kč".
   */
  const formatted = new Intl.NumberFormat(intlTag(locale), {
    style: 'currency',
    currency: value.currency,
    maximumFractionDigits: 0,
  }).format(Number(value.amount));
  return <>{formatted}</>;
}

function CommissionCard({ card }: { readonly card: OverviewCardOf<'commission'> }): React.JSX.Element | null {
  const t = useT();
  if (card.data === null) return null;
  return (
    <>
      <p className="rh-ov-figure" data-testid="prehled-provize-castka">
        <Money value={card.data.thisMonth} />
      </p>
      {/* „Bez cíle karta bez čáry" (zadani) — veta o cili se proste nekresli. */}
      {card.data.goal !== null ? (
        <p className="rh-ov-card__sub" data-testid="prehled-provize-cil">
          {t('webAdmin.prehled.commission.goal')} <Money value={card.data.goal} />
        </p>
      ) : null}
      <Bars
        items={card.data.trend.map((row) => ({ label: row.month.slice(5), value: Number(row.amount) }))}
      />
    </>
  );
}

function OfficeSiteCard({ card }: { readonly card: OverviewCardOf<'officeSite'> }): React.JSX.Element | null {
  const t = useT();
  const count = useCount();
  if (card.data === null) return null;
  return (
    <>
      {/* `null` navstevy = web je, merení jeste ne. Nula by lhala. */}
      {card.data.visits30d === null ? (
        <p className="rh-ov-note" data-testid="prehled-web-bez-mereni">
          {t('webAdmin.prehled.officeSite.noAnalytics')}
        </p>
      ) : (
        <p className="rh-ov-figure" data-testid="prehled-web-navstevy">
          {count(card.data.visits30d)}
        </p>
      )}
      <p className="rh-ov-card__sub" data-testid="prehled-web-poptavky">
        {t('webAdmin.prehled.officeSite.demands', { count: card.data.demandsFromSite })}
      </p>
    </>
  );
}

function AdSpendCard({ card }: { readonly card: OverviewCardOf<'adSpend'> }): React.JSX.Element | null {
  const t = useT();
  if (card.data === null) return null;
  return (
    <>
      <p className="rh-ov-figure" data-testid="prehled-inzerce-utrata">
        <Money value={card.data.thisMonth} />
      </p>
      {card.data.costPerDemand !== null ? (
        <p className="rh-ov-card__sub" data-testid="prehled-inzerce-cena">
          {t('webAdmin.prehled.adSpend.perDemand')} <Money value={card.data.costPerDemand} />
        </p>
      ) : null}
    </>
  );
}

function BrokersCard({ card }: { readonly card: OverviewCardOf<'brokers'> }): React.JSX.Element | null {
  const t = useT();
  if (card.data === null) return null;
  return (
    <ul className="rh-ov-rows" data-testid="prehled-maklerí-seznam">
      {card.data.items.map((item) => (
        <li className="rh-ov-row" key={item.userId}>
          <span>{item.name === '' ? item.userId.slice(0, 8) : item.name}</span>
          <span className="rh-ov-row__time">
            {t('webAdmin.prehled.brokers.cases', { count: item.openCases })}
          </span>
          <Money value={item.commissionThisMonth} />
        </li>
      ))}
    </ul>
  );
}

function CreditsCard({ card }: { readonly card: OverviewCardOf<'credits'> }): React.JSX.Element | null {
  const t = useT();
  const locale = useLocale();
  if (card.data === null) return null;
  return (
    <>
      <ul className="rh-ov-rows" data-testid="prehled-kredity-seznam">
        {card.data.items.map((item) => (
          <li className="rh-ov-row" key={item.productCode}>
            <span>{item.productCode}</span>
            <span className="rh-ov-figure__note">{item.balance}</span>
          </li>
        ))}
      </ul>
      {card.data.membershipExpiresAt !== null ? (
        <p className="rh-ov-card__sub" data-testid="prehled-predplatne-do">
          {t('webAdmin.prehled.credits.until', {
            date: new Date(card.data.membershipExpiresAt).toLocaleDateString(locale),
          })}
        </p>
      ) : null}
    </>
  );
}

/**
 * Krok GRAF6 nalez 4 — KARTA BEZ DAT. Tvar urcuje `CARD_EMPTY_SHAPE`, text je
 * tentyz klic `cards.<id>.empty`, jaky se kreslil pred GRAF6, a vyzva vede tam,
 * kde se chybejici data poridi (`CARD_LINK` — tatáz mapa, jakou pouziva stav
 * „nepripojeno"). Zadny novy text, zadna druha tabulka cilu.
 */
function CardEmpty({ id }: { readonly id: OverviewCardId }): React.JSX.Element {
  const t = useT();
  const count = useCount();
  const shape = CARD_EMPTY_SHAPE[id];
  return (
    <>
      {shape === undefined ? null : (
        <p className="rh-ov-figure rh-ov-figure--zero" data-testid={`prehled-nula-${id}`}>
          {count(0)}
        </p>
      )}
      <p className="rh-ov-note" data-testid={`prehled-stav-${id}`}>
        {cardText(t, id, 'empty')}
      </p>
      {shape === 'figure-chart' ? (
        <div className="rh-ov-bars rh-ov-bars--muted" data-testid={`prehled-graf-nula-${id}`} aria-hidden="true">
          {EMPTY_CHART_BARS.map((index) => (
            <div className="rh-ov-bar" key={index}>
              <div className="rh-ov-bar__fill rh-ov-bar__fill--muted" />
            </div>
          ))}
        </div>
      ) : null}
      {/* Jedna kratka vyzva — aby karta nova kancelare nebyla slepa ulicka. */}
      <p className="rh-a2-card__foot">
        <Link to={CARD_LINK[id]} className="rh-a2-link" data-testid={`prehled-vyzva-${id}`}>
          {cardText(t, id, 'action')}
        </Link>
      </p>
    </>
  );
}

/** Obsah karty podle jejiho id — jedno misto, kde se id prevadi na sazbu. */
function CardBody({
  card,
  onTaskDone,
  busyTaskId,
}: {
  readonly card: OverviewCard;
  readonly onTaskDone: (taskId: string) => void;
  readonly busyTaskId: string | null;
}): React.JSX.Element | null {
  switch (card.id) {
    case 'burning':
      return <BurningCard card={card} />;
    case 'nextStep':
      return <NextStepCard card={card} />;
    case 'today':
      return <TodayCard card={card} onTaskDone={onTaskDone} busyTaskId={busyTaskId} />;
    case 'cases':
      return <CasesCard card={card} />;
    case 'demands':
      return <DemandsCard card={card} />;
    case 'portals':
      return <PortalsCard card={card} />;
    case 'radar':
      return <RadarCard card={card} />;
    case 'commission':
      return <CommissionCard card={card} />;
    case 'officeSite':
      return <OfficeSiteCard card={card} />;
    case 'adSpend':
      return <AdSpendCard card={card} />;
    case 'brokers':
      return <BrokersCard card={card} />;
    case 'credits':
      return <CreditsCard card={card} />;
  }
}

export function OverviewBoard(): React.JSX.Element | null {
  const t = useT();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: OVERVIEW_QUERY_KEY, queryFn: fetchOverview });

  /*
   * Preskladani a skryvani: volba se ulozi na server a obrazovka se prekresli
   * z ODPOVEDI endpointu, ne z lokalniho stavu — poradi tak po obnove
   * stranky nezacne skakat (server zna i karty, ktere role vidi).
   */
  const save = useMutation({
    mutationFn: async (prefs: OverviewCardPrefs) => saveOverviewCards(prefs),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: OVERVIEW_QUERY_KEY });
    },
  });

  /*
   * Odskrtnuti ukolu primo z karty „Dnes" (prevzato z programu Dneška pri
   * slouceni v GRAF2). Prechod je `transitionTask` — tentyz, jaky dela tabule
   * Úkoly; po nem se souhrn nacte znovu, aby ukol z karty zmizel.
   */
  const [busyTaskId, setBusyTaskId] = React.useState<string | null>(null);
  const markTaskDone = (taskId: string): void => {
    setBusyTaskId(taskId);
    void transitionTask(taskId, 'done')
      .then(() => queryClient.invalidateQueries({ queryKey: OVERVIEW_QUERY_KEY }))
      .catch(() => {
        // Souhrn se nemeni, karta dal ukazuje ukol jako nehotovy — chyba se
        // ukaze na tabuli Úkoly, kde se s ukolem skutecne pracuje.
      })
      .finally(() => {
        setBusyTaskId(null);
      });
  };

  /*
   * Krok GRAF2t: NEPOVEDENE NACTENI SE HLASI. Do slouceni mel Dnesek pro
   * program dne i schuzky vlastni chybove hlaseni (`dnesek-tasks-error`,
   * `dnesek-meetings-error`); tabule stoji na jednom dotazu, takze by misto
   * nich zustala prazdna obrazovka a „dnes nic nemám" by se nedalo rozeznat
   * od „data nedosla".
   */
  if (query.isError) {
    return (
      <p role="alert" className="rh-a2-error" data-testid="prehled-chyba">
        {t('webAdmin.prehled.loadError')}
      </p>
    );
  }
  if (query.data === undefined) return null;
  const { cards, hidden } = query.data;
  const order = cards.map((card) => card.id);

  const hide = (id: OverviewCardId): void => {
    save.mutate({ hidden: [...hidden, id], order });
  };
  const show = (id: OverviewCardId): void => {
    save.mutate({ hidden: hidden.filter((other) => other !== id), order });
  };
  const moveEarlier = (id: OverviewCardId): void => {
    const index = order.indexOf(id);
    if (index <= 0) return;
    const next = [...order];
    const previous = next[index - 1] as OverviewCardId;
    next[index - 1] = id;
    next[index] = previous;
    save.mutate({ hidden, order: next });
  };

  return (
    <div data-testid="prehled-tabule">
      <div className="rh-ov-grid">
        {cards.map((card) =>
          /*
           * Krok GRAF4 nalez 2 — „Co hoří" BEZ OBSAHU SE NEZOBRAZUJE. Predloha
           * zna jen pruh s vyctem; prazdna karta s nadpisem „Co hoří" a vetou
           * „nic nehoří" by na Prehledu drzela misto a — jak ukazal dozor —
           * rikala opak toho, co veta nad tabuli. Pruh si taky kresli vlastni
           * plochu, takze nejde pres `CardShell` (karta se sklem a hlavickou).
           */
          card.id === 'burning' ? (
            card.state === 'data' || card.state === 'error' ? (
              <div
                className="rh-ov-cell"
                key={card.id}
                style={{ ['--rh-ov-span' as string]: String(CARD_SPAN.burning) }}
                data-testid="prehled-karta-burning"
              >
                {card.state === 'data' ? (
                  <CardBody card={card} onTaskDone={markTaskDone} busyTaskId={busyTaskId} />
                ) : (
                  /*
                   * Krok PREHLED1: pruh „Co hoří" se bez obsahu nekresli
                   * (nalez GRAF4 c. 2), ALE nepovedeny vypocet neni prazdno —
                   * tise schovat pruh by rikalo „nic nehoří", i kdyz to nikdo
                   * nezmeril.
                   */
                  <p className="rh-ov-note" data-testid="prehled-stav-burning">
                    {t('webAdmin.prehled.cardError')}
                  </p>
                )}
              </div>
            ) : null
          ) : (
          <CardShell
            key={card.id}
            id={card.id}
            title={cardText(t, card.id, 'title')}
            tools={
              <>
                <button
                  type="button"
                  className="rh-a2-btn rh-a2-btn--ghost"
                  onClick={() => {
                    moveEarlier(card.id);
                  }}
                  aria-label={t('webAdmin.prehled.moveEarlier', {
                    card: cardText(t, card.id, 'title'),
                  })}
                  data-testid={`prehled-posunout-${card.id}`}
                >
                  <ChevronLeft size={16} strokeWidth={2} aria-hidden />
                </button>
                <button
                  type="button"
                  className="rh-a2-btn rh-a2-btn--ghost"
                  onClick={() => {
                    hide(card.id);
                  }}
                  aria-label={t('webAdmin.prehled.hide', { card: cardText(t, card.id, 'title') })}
                  data-testid={`prehled-skryt-${card.id}`}
                >
                  <EyeOff size={16} strokeWidth={2} aria-hidden />
                </button>
              </>
            }
          >
            {card.state === 'data' ? (
              <CardBody card={card} onTaskDone={markTaskDone} busyTaskId={busyTaskId} />
            ) : card.state === 'empty' ? (
              /* Krok GRAF6 nalez 4: prazdna karta ma tvar predlohy, ne jen vetu. */
              <CardEmpty id={card.id} />
            ) : card.state === 'error' ? (
              /*
               * Krok PREHLED1: vypocet TETO karty se nepovedl. Zustava sklem
               * a malym textem — cervena plocha by z jedne vadne agregace
               * udelala poplach pres celou tabuli, a ostatnich jedenact karet
               * pritom plati.
               */
              <p className="rh-ov-note" data-testid={`prehled-stav-${card.id}`}>
                {t('webAdmin.prehled.cardError')}
              </p>
            ) : (
              <>
                <p className="rh-ov-note" data-testid={`prehled-stav-${card.id}`}>
                  {cardText(t, card.id, 'disconnected')}
                </p>
                {/* Nepripojeny zdroj NABIZI, co s tim. */}
                <Link to={CARD_LINK[card.id]} className="rh-a2-btn">
                  {cardText(t, card.id, 'action')}
                </Link>
              </>
            )}
          </CardShell>
          ),
        )}
      </div>

      {hidden.length > 0 ? (
        <div className="rh-ov-hidden" data-testid="prehled-skryte">
          <span className="rh-ov-card__sub">{t('webAdmin.prehled.hiddenLabel')}</span>
          {hidden.map((id) => (
            <button
              key={id}
              type="button"
              className="rh-a2-btn rh-a2-btn--ghost"
              onClick={() => {
                show(id);
              }}
              data-testid={`prehled-zobrazit-${id}`}
            >
              {cardText(t, id, 'title')}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
